import type { RootState } from 'src/redux/store';

import { useDispatch } from 'react-redux';
import { useMemo, useCallback, useState, useEffect } from 'react';

import { useAppSelector } from 'src/hook/hooks';
import { setActiveSubscription } from 'src/pages/setting/slice/settingSlice';
import { useGetSubscriptionListMutation } from 'src/pages/setting/slice/settingApi';

import { PremissionContext } from './permissions-context';

type PermissionProviderProps = {
  children: React.ReactNode;
};

export function PremissionProvider({ children }: PermissionProviderProps) {
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const [getSubscriptionList] = useGetSubscriptionListMutation();

  const [activePlan, setActivePlan] = useState<boolean>(false);
  const [isActiveSubscriptionLoading, setIsActiveSubscriptionLoading] = useState<boolean>(true); // ✅ own loader
  const dispatch = useDispatch();

  // ------------------------------
  // ✅ Subscription check
  // ------------------------------
  const fetchSubscriptions = useCallback(async () => {
    try {
      if (!(loginApiData as any)?.tenant?.id) {
        console.warn('⚠️ No tenantId found in loginApiData');
        setActivePlan(false);
        setIsActiveSubscriptionLoading(false);
        return;
      }

      setIsActiveSubscriptionLoading(true); // ⏳ start loading

      const response = await getSubscriptionList({
        filters: [
          { fieldname: 'status', search: 'active' },
          { fieldname: 'tenantId', search: (loginApiData as any).tenant.id },
        ],
        globalSearch: '',
        page: 1,
        limit: 10,
      }).unwrap();

      if (response?.success && response?.data?.subscription?.plan) {
        dispatch(setActiveSubscription(response.data.subscription));
        setActivePlan(true);
      } else {
        dispatch(setActiveSubscription(null));
        setActivePlan(false);
      }
    } catch (err: any) {
      console.error('❌ Failed to fetch subscriptions:', err);
      dispatch(setActiveSubscription(null));
      setActivePlan(false);
    } finally {
      setIsActiveSubscriptionLoading(false); // ✅ end loading
    }
  }, [loginApiData, getSubscriptionList, dispatch]);

  useEffect(() => {
    if (
      loginApiData &&
      'tenant' in loginApiData &&
      typeof loginApiData.tenant === 'object' &&
      loginApiData.tenant !== null &&
      'name' in loginApiData.tenant &&
      (loginApiData.tenant as { name?: string }).name &&
      (loginApiData.tenant as { name?: string }).name !== 'MASTER'
    ) {
      fetchSubscriptions();
    }
  }, [loginApiData, fetchSubscriptions]);

  // ------------------------------
  // ✅ Permissions logic
  // ------------------------------
  const getPermissions = () => {
    if (loginApiData && 'permissions' in loginApiData) return loginApiData.permissions;
    // if (loginApiData && 'permissions' in loginApiData)
    //   return {
    // dashboard: {
    //   view: true,
    // },
    // company: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    //   import: true,
    //   export: true,
    // },
    // contact: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    //   import: true,
    //   export: true,
    // },
    // inbox: {
    //   view: true,
    // },
    // conversation: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   export: true,
    // },
    // campaign: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // channel: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // user: {
    //   view: true,
    //   create: true,
    //   edit: true,
    // },
    // label: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // team: {
    //   view: true,
    //   create: true,
    //   edit: true,
    // },
    // template: {
    //   view: true,
    //   create: true,
    // },
    // call: {
    //   view: true,
    //   export: true,
    // },
    // tenants: {
    //   view: true,
    //   list: true,
    //   edit: true,
    //   create: true,
    //   delete: true,
    // },
    // ticket: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    //   export: true,
    // },
    // organization: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // bill: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // tamplate: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // webhook: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // discount: {
    //   view: true,
    //   create: true,
    //   edit: true,
    //   delete: true,
    // },
    // };
    try {
      const persistRoot = localStorage.getItem('persist:root');
      if (!persistRoot) return null;

      const userPersistData = JSON.parse(persistRoot);
      const authData = JSON.parse(userPersistData?.auth ?? '{}');

      return authData?.loginApiData?.permissions || null;
    } catch (err) {
      console.error('Failed to parse persisted permissions:', err);
      return null;
    }
  };

  const permissions = useMemo(() => getPermissions(), [loginApiData]);

  // ------------------------------
  // ✅ Navigation filtering
  // ------------------------------
  const handleNavPermissionCheck = (navList: any[]) => {
    if (!permissions) return navList;

    // helper to check single or multiple ids
    const checkPermission = (id: string | string[]): boolean => {
      const check = (m: string) => (m === 'setting' ? true : !!permissions?.[m]?.view);

      return Array.isArray(id) ? id.some(check) : check(id);
    };

    return navList.map((group) => {
      const filteredItems = (group.items || [])
        .map((item: any) => {
          const key = typeof item.id === 'string' ? item.id.toLowerCase() : item.id;

          if (key === 'setting' || (Array.isArray(key) && key.includes('setting'))) {
            const visibleChildren = (item.children || []).flatMap((child: any) => {
              const childId = typeof child.id === 'string' ? child.id.toLowerCase() : child.id;

              // ✅ Bill special case
              if (
                (childId === 'bill' ||
                  childId === 'billing' ||
                  (Array.isArray(childId) &&
                    (childId.includes('bill') || childId.includes('billing')))) &&
                loginApiData &&
                'tenant' in loginApiData &&
                typeof loginApiData.tenant === 'object' &&
                loginApiData.tenant !== null &&
                'name' in loginApiData.tenant &&
                (loginApiData.tenant as { name?: string }).name &&
                (loginApiData as { role?: string }).role === 'Admin' &&
                (loginApiData.tenant as { name?: string }).name !== 'MASTER'
              ) {
                // ✅ Always allow bill/billing for Admins that are not MASTER
                return [child];
              }

              // ✅ Normal child permission check
              return checkPermission(childId) ? [child] : [];
            });

            return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
          }
          if ((key === 'report' || (Array.isArray(key) && key.includes('report'))) && loginApiData &&
            (loginApiData as { role?: string }).role === 'Admin') {
            const visibleChildren = (item.children || []).filter((child: any) => {
              const childId = typeof child.id === 'string' ? child.id.toLowerCase() : child.id;
              return checkPermission(childId);
            });
            return visibleChildren.length > 0 ? { ...item, children: visibleChildren } : null;
          }

          // ✅ Generic item permission check
          return checkPermission(key) && key !== 'report' ? item : null;
        })
        .filter(Boolean);

      return { ...group, items: filteredItems };
    });
  };

  // ------------------------------
  // ✅ Module permission check
  // ------------------------------
  // ✅ Handles string OR string[]
  const hasPermission = (module: string | string[]): boolean => {
    const check = (m: string) =>
      m === 'setting' || ['bill', 'billing'].includes(m) ? true : !!permissions?.[m]?.view;

    if (Array.isArray(module)) {
      // true if ANY module in array has permission
      return module.some(check);

      // Or require ALL → return module.every(check);
    }

    return check(module);
  };

  // ✅ Handles string OR string[]
  function hasModuleActionPermission(moduleName: string | string[], actionType: string): boolean {
    const check = (m: string) => {
      const updateModule = m === 'account' ? 'company' : m;
      return !!permissions?.[updateModule]?.[actionType];
    };

    if (Array.isArray(moduleName)) {
      // true if ANY module in array allows the action
      return moduleName.some(check);

      // Or require ALL → return moduleName.every(check);
    }

    return check(moduleName);
  }

  const contextValue = useMemo(
    () => ({
      handleNavPremissionCheck: handleNavPermissionCheck,
      hasPermission,
      hasModuleActionPermission,
      permissions,
    }),
    [permissions]
  );

  return <PremissionContext.Provider value={contextValue}>{children}</PremissionContext.Provider>;
}
